import React, { useEffect, useState } from 'react'

/**
 * Compact paginator: «  [3] of 17  »
 *
 * The page number is an input — type a page and press Enter (or blur) to jump.
 *
 * Backwards compatible with the previous ReactPaginate-based API — every
 * existing caller passes { pageCount, handlePageClick, forcePage } and
 * handlePageClick still receives a { selected } object, exactly as before.
 *
 * Optional: pass `totalItems` and `pageSize` to also show an item range
 * ("1-17 of 17") alongside the page jumper.
 */
function Paginator({
  pageCount,
  handlePageClick,
  forcePage = 0,
  totalItems = null,
  pageSize = null,
}) {
  const pages = Number(pageCount) || 0;
  const current = Math.min(Math.max(Number(forcePage) || 0, 0), Math.max(pages - 1, 0));

  const [pageInput, setPageInput] = useState(current + 1);

  // keep the field in sync when the page changes from elsewhere (prev/next, parent)
  useEffect(() => {
    setPageInput(current + 1);
  }, [current]);

  const goTo = (idx) => {
    if (idx < 0 || idx > pages - 1 || idx === current) return;
    if (handlePageClick) handlePageClick({ selected: idx });
  };

  const commitInput = () => {
    let n = parseInt(pageInput, 10);
    if (isNaN(n)) {
      setPageInput(current + 1);
      return;
    }
    n = Math.min(Math.max(n, 1), pages || 1);
    setPageInput(n);
    goTo(n - 1);
  };

  let itemRange = null;
  if (totalItems != null && pageSize) {
    const total = Number(totalItems) || 0;
    const start = total === 0 ? 0 : current * pageSize + 1;
    const end = Math.min((current + 1) * pageSize, total);
    itemRange = `${start}-${end} of ${total}`;
  }

  return (
    <nav className="compact-paginator" aria-label="pagination">
      <button
        type="button"
        className="cp-btn"
        disabled={current <= 0}
        onClick={() => goTo(current - 1)}
        aria-label="Previous page"
      >
        <sl-icon library="lucide" name="chevron-left"></sl-icon>
      </button>
      <span className="cp-label">
        <input
          type="number"
          className="cp-input"
          min={1}
          max={pages || 1}
          value={pageInput}
          aria-label="Page number"
          onChange={(e) => setPageInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitInput();
              e.target.blur();
            }
          }}
          onBlur={commitInput}
        />
        <span className="cp-of">of {pages}</span>
        {itemRange ? <span className="cp-items">({itemRange})</span> : null}
      </span>
      <button
        type="button"
        className="cp-btn"
        disabled={current >= pages - 1}
        onClick={() => goTo(current + 1)}
        aria-label="Next page"
      >
        <sl-icon library="lucide" name="chevron-right"></sl-icon>
      </button>
    </nav>
  );
}

export default Paginator;
